<template>
  <SideMenu
    ref="sideMenu"
    :heading="`${$lang(
      'checkout',
      'shopping_cart_heading'
    )} (${cartProductCount})`"
    :side="'right'"
    :has-fixed-footer="true"
    @on-side-menu-close="cartStore.showCartMenu = false"
  >
    <div
      class="bg-main h-full flex flex-col"
      :class="{
        'justify-center': items.length === 0,
        'pb-253': hasFreeShipping,
        'pb-208': !hasFreeShipping,
      }"
    >
      <div
        v-if="items.length > 0"
        class="px-24 overflow-auto border-b border-default flex flex-col flex-1"
      >
        <CheckoutProductItem
          v-for="item in items"
          :key="item.item"
          :item="item"
          :is-checkout="false"
          class="py-16"
        />
      </div>
      <div v-else class="md:p-24 p-16">
        {{ $lang('checkout', 'empty_cart') }}
      </div>
      <div class="absolute right-0 bottom-0 left-0 border-t">
        <div
          v-if="hasFreeShipping && items.length > 0 && !freeShipping"
          class="
            py-8
            flex
            justify-center
            items-center
            text-small
            bg-picker
            border-b
          "
        >
          <fa
            v-if="freeShippingCount <= 0"
            class="mr-8"
            :icon="['fal', 'check']"
          />
          {{
            freeShippingCount > 0
              ? `${formatPriceUnit(freeShippingCount, country)} ${$lang(
                'checkout',
                'free_shipping_counter'
              )}`
              : $lang('checkout', 'free_shipping_completed')
          }}
        </div>
        <div 
          v-else
          class="w-full px-24 pt-12 border-b"
        >
          <div class="flex justify-between items-center w-full mb-16">
            <div class="text-small">
              {{ $lang('checkout', 'shipping_cost') }}:
            </div>
            <div
              class="text-base-medium"
              :class="{ 'text-grey': cartStore.cartIsUpdating }"
            >
              {{ cartData.shippingCost }}
            </div>
          </div>
        </div>
        <div class="w-full px-24 pt-24 pb-32 bg-picker">
          <div class="flex justify-between items-center w-full mb-16">
            <div class="text-small">
              {{ $lang('checkout', 'products_sum') }}:
            </div>
            <div
              class="text-base-medium"
              :class="{ 'text-grey': cartStore.cartIsUpdating }"
            >
              {{ cartData.totalCost }}
            </div>
          </div>
          <ButtonComponent
            :link-url="config.checkoutRoute"
            :text="$lang('checkout', 'full_checkout_btn')"
            is-block
            :disabled="items.length === 0"
          />
        </div>
      </div>
    </div>
  </SideMenu>
</template>

<script setup lang="ts">
import type { Ref } from 'vue';
import { useCartStore } from '~/store/cart';
import { useConfigStore } from '~/store/config';
import SideMenu from '~/components/navigation/SideMenu.vue';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import CartItem from '~/models/cart-item';
import type { CartItemType } from '~/constants/types/cart-item';
import type { ShippingMethod } from '~/constants/types/shipping-method';
import { formatPriceUnit } from '~/util/price-formatting';
import CheckoutProductItem from '~/components/cart/CheckoutProductItem.vue';
const { centraPut } = useApiFetch();
const { sellerCookieOptions } = useCookieConfig();

const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions) as Ref<boolean>;

const config = useConfigStore();
const cartStore = useCartStore();
const sideMenu = ref<typeof SideMenu | null>(null);
onMounted(()=> {
  if (sideMenu.value) {
    sideMenu.value.openOverlay();
  }
  setDeliveryMethod();
});

const setDeliveryMethod = async()=> {
  const res = ref();
  if (isSeller.value) {
    res.value = await centraPut('/shipping-methods/seller-homedelivery/');
  } else {
    let defaultshippingMethod = config.shippingMethods[0].centra_shipping_method;
    res.value = await centraPut('/shipping-methods/' + defaultshippingMethod);
  }
  if (res.value) {
    cartStore._cartData = res.value;
  }
};

const cartProductCount = computed<number>(() => {
  return cartStore.cartData.totalItemQuantity || 0;
});

const cartData = computed(()=> {
  return cartStore.cartData;
});

const items = computed<CartItem[]>(()=> {
  const cartData = cartStore.cartData;
  return (
    cartData.items.map(
      (item: CartItemType) => new CartItem({ cartItem: item })
    ) || []
  );
});

const freeShipping = computed(()=> {
  return items.value[0]?.isGiftCertificate || false;
});

const wpSelectedShippingMethod = computed<ShippingMethod | null>(()=> {
  const wpShippingMethod = config.shippingMethods?.find(
    (method) =>
      cartStore.cartData.selectedShippingMethod === method.centra_shipping_method
  );
  return wpShippingMethod || null;
});

const hasFreeShipping = computed(()=> {
  if (wpSelectedShippingMethod.value && !isSeller) {
    return wpSelectedShippingMethod.value.has_free_shipping_limit;
  } else {
    return false;
  }
});

const freeShippingCount = computed<number>(()=> {
  if (wpSelectedShippingMethod.value) {
    const freeShippingLimit = Number(
      wpSelectedShippingMethod.value.free_shipping_limit
    );
    return freeShippingLimit - cartStore.cartData.totalItemsCostAsNumber || 0;
  } else {
    return 0;
  }
});

const country = computed(()=> {
  return config.market?.country || '';
});

</script>

<style scoped lang="postcss">
</style>
